import { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import Gate from './components/gate/gate';
import Board from './page/board';
import Curate from './page/curate';
import ShowPage from './page/show';
import { UserSettingsProvider, useUserSettingsContext } from './utils/context';
import { resolveHost, showByKey, Show } from './shows';

// The "workshop" setup (jaysilvr, 2026-10-21):
//   - a show's production hostname (pll.silvr.shop) renders that show's client page at "/" and nothing else;
//   - the board (dev.silvr.shop, localhost) renders "/" = the shows, "/<key>" = that show's client page
//     exactly as production will, "/<key>/curate" = its curation page (or, for a show with `curatedOn`,
//     a redirect to that show's);
//   - any other hostname gets a plain "nothing here" page.
// The board and the previews sit behind the gate passcode; curate asks for the curation passcode as well.

const ShowRoute = ({ show }: { show: Show }) => {
  const Page = show.Page ?? ShowPage;
  return (
    <Suspense fallback={<div style={{ minHeight: '100vh', background: '#000' }} />}>
      <Page show={show} />
    </Suspense>
  );
};

const ShowByKey = ({ curate = false }: { curate?: boolean }) => {
  const { key } = useParams();
  const show = showByKey(key);
  if (!show) return <Navigate to="/" replace />;
  if (curate && show.curatedOn) return <Navigate to={`/${show.curatedOn}/curate`} replace />;
  return curate ? <Curate show={show} /> : <ShowRoute show={show} />;
};

const Gated = ({ children }: { children: React.ReactNode }) => {
  const { userSettings } = useUserSettingsContext();
  return userSettings?.isLoggedIn ? <>{children}</> : <Gate />;
};

const NothingHere = () => (
  <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#000', color: '#9ca3af', fontFamily: 'Figtree, sans-serif' }}>
    <p>Nothing here.</p>
  </main>
);

const App = () => {
  const host = resolveHost();
  return (
    <UserSettingsProvider>
      <BrowserRouter>
        {host.mode === 'show' ? (
          <Routes>
            <Route
              path="/"
              element={
                <Gated>
                  <ShowRoute show={host.show} />
                </Gated>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : host.mode === 'board' ? (
          <Routes>
            <Route
              path="/"
              element={
                <Gated>
                  <Board />
                </Gated>
              }
            />
            <Route
              path="/:key"
              element={
                <Gated>
                  <ShowByKey />
                </Gated>
              }
            />
            <Route
              path="/:key/curate"
              element={
                <Gated>
                  <ShowByKey curate />
                </Gated>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : (
          <Routes>
            <Route path="*" element={<NothingHere />} />
          </Routes>
        )}
      </BrowserRouter>
    </UserSettingsProvider>
  );
};

export function renderToDom(container: HTMLElement) {
  createRoot(container).render(<App />);
}
